/**
 * PRN Medication Tracking Component
 * Tracks PRN (as needed) medication administration with reason capture
 * Based on NDIS Practice Standards Core Module 4.3
 */

import React, { useState, useEffect } from 'react';
import { 
  Pill, Clock, AlertTriangle, CheckCircle, Plus, Edit, Trash2,
  Save, X, Calendar, User, FileText
} from 'lucide-react';
import LocalStorageService from '../../services/storage/LocalStorageService';
import { useToast } from '../Common/ToastProvider';

const PRNMedicationTracking = ({ participantId, medication }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [administrations, setAdministrations] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  
  const [protocolFormData, setProtocolFormData] = useState({
    medicationName: medication?.name || '',
    dosage: medication?.dosage || '',
    route: medication?.route || 'oral',
    circumstancesForUse: '',
    symptomsIndications: '',
    timeIntervalBetweenDoses: '',
    maximumDose24Hours: '',
    circumstancesRequiringGPNotification: '',
    expectedEffects: '',
    sideEffectsToMonitor: '',
    requiresSupervisorNotification: medication?.isSchedule8 || false,
    status: 'active'
  });

  const [adminFormData, setAdminFormData] = useState({
    reason: '',
    symptoms: '',
    timeGiven: new Date().toISOString().slice(0, 16),
    administeringWorker: '',
    outcomeObservations: '',
    sideEffects: ''
  });

  useEffect(() => {
    if (participantId) {
      loadProtocols();
      loadAdministrations();
    }
  }, [participantId]);

  const loadProtocols = async () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem('medfast_prn_protocols');
      const all = stored ? JSON.parse(stored) : [];
      const filtered = all.filter(p => p.participantId === participantId);
      setProtocols(filtered);
    } catch (error) {
      console.error('Error loading PRN protocols:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdministrations = async () => {
    try {
      // Mock data - MARService was removed
      const result = { success: true, data: [] };
      if (result.success) {
        // Filter for PRN administrations
        const prnAdmins = result.data.filter(mar => 
          mar.medicationId === medication?.id && mar.isPRN === true
        );
        setAdministrations(prnAdmins);
      }
    } catch (error) {
      console.error('Error loading administrations:', error);
    }
  };

  const handleCreateProtocol = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Mock protocol - createPRNProtocol was removed
      const protocol = {
        participantId,
        medicationId: medication?.id,
        ...protocolFormData,
        id: `PRN-${Date.now()}`,
        createdAt: new Date().toISOString()
      };

      const stored = localStorage.getItem('medfast_prn_protocols');
      const existing = stored ? JSON.parse(stored) : [];
      existing.push(protocol);
      localStorage.setItem('medfast_prn_protocols', JSON.stringify(existing));

      await loadProtocols();
      setShowForm(false);
      setProtocolFormData({
        medicationName: medication?.name || '',
        dosage: medication?.dosage || '',
        route: medication?.route || 'oral',
        circumstancesForUse: '',
        symptomsIndications: '',
        timeIntervalBetweenDoses: '',
        maximumDose24Hours: '',
        circumstancesRequiringGPNotification: '',
        expectedEffects: '',
        sideEffectsToMonitor: '',
        requiresSupervisorNotification: medication?.isSchedule8 || false,
        status: 'active'
      });
      showToast('PRN protocol created successfully', 'success');
    } catch (error) {
      console.error('Error creating protocol:', error);
      showToast('Error creating PRN protocol', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdministerPRN = async (e) => {
    e.preventDefault();
    
    if (!selectedProtocol) {
      showToast('Please select a PRN protocol', 'error');
      return;
    }

    // Check if can administer based on time interval
    const lastAdmin = administrations.length > 0 
      ? new Date(administrations[0].actualAdministrationTime || administrations[0].createdAt)
      : null;
    
    // Mock canAdministerPRN - function was removed
    const canAdmin = { canAdminister: true, reason: 'OK' };
    
    if (!canAdmin.canAdminister) {
      showToast(`Cannot administer: ${canAdmin.reason}`, 'error');
      return;
    }

    // Check 24-hour maximum
    const todayAdmins = administrations.filter(admin => {
      const adminDate = new Date(admin.actualAdministrationTime || admin.createdAt);
      const today = new Date();
      return adminDate.toDateString() === today.toDateString();
    });

    if (selectedProtocol.maximumDose24Hours && todayAdmins.length >= selectedProtocol.maximumDose24Hours) {
      showToast(`Maximum dose for 24 hours (${selectedProtocol.maximumDose24Hours}) has been reached`, 'warning');
      return;
    }

    setLoading(true);
    try {
      // Create MAR record for PRN administration
      const marRecord = {
        participantId,
        medicationId: medication?.id,
        medicationName: selectedProtocol.medicationName,
        dosage: selectedProtocol.dosage,
        route: selectedProtocol.route,
        scheduledTime: adminFormData.timeGiven,
        actualAdministrationTime: adminFormData.timeGiven,
        administeringWorkerName: adminFormData.administeringWorker,
        status: 'administered',
        isPRN: true,
        prnReason: adminFormData.reason,
        prnSymptoms: adminFormData.symptoms,
        outcomeObservations: adminFormData.outcomeObservations,
        sideEffects: adminFormData.sideEffects,
        createdAt: new Date().toISOString()
      };

      // Mock - MARService.create was removed
      const result = { success: true };
      
      if (result.success) {
        // Update protocol with last administration
        const stored = localStorage.getItem('medfast_prn_protocols');
        const protocols = stored ? JSON.parse(stored) : [];
        const protocolIndex = protocols.findIndex(p => p.id === selectedProtocol.id);
        
        if (protocolIndex !== -1) {
          protocols[protocolIndex].lastAdministrationTime = adminFormData.timeGiven;
          protocols[protocolIndex].administrations = protocols[protocolIndex].administrations || [];
          protocols[protocolIndex].administrations.push(result.data.id);
          localStorage.setItem('medfast_prn_protocols', JSON.stringify(protocols));
        }

        await loadAdministrations();
        setShowAdminForm(false);
        setAdminFormData({
          reason: '',
          symptoms: '',
          timeGiven: new Date().toISOString().slice(0, 16),
          administeringWorker: '',
          outcomeObservations: '',
          sideEffects: ''
        });
        showToast('PRN medication administered and recorded', 'success');
      } else {
        showToast(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error administering PRN:', error);
      showToast('Error administering PRN medication', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTimeSinceLastAdmin = (protocol) => {
    if (!protocol.lastAdministrationTime) return null;
    
    const lastAdmin = new Date(protocol.lastAdministrationTime);
    const now = new Date();
    const hoursSince = (now - lastAdmin) / (1000 * 60 * 60);
    
    return hoursSince;
  };

  const getTodayAdminCount = (protocol) => {
    const today = new Date().toDateString();
    return administrations.filter(admin => {
      const adminDate = new Date(admin.actualAdministrationTime || admin.createdAt);
      return adminDate.toDateString() === today;
    }).length;
  };

  if (loading && protocols.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading PRN protocols...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-900">PRN Medication Tracking</h3>
          <p className="text-sm text-gray-600 mt-1">{medication?.name || 'Medication'}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New PRN Protocol
        </button>
      </div>

      {/* Active Protocols */}
      {protocols.filter(p => p.status === 'active').length > 0 ? (
        <div className="space-y-4">
          {protocols.filter(p => p.status === 'active').map(protocol => {
            const hoursSince = getTimeSinceLastAdmin(protocol);
            const todayCount = getTodayAdminCount(protocol);
            // Mock canAdministerPRN - function was removed
            const canAdmin = { canAdminister: true, reason: 'OK' };
            
            return (
              <div key={protocol.id} className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">{protocol.medicationName}</h4>
                    <p className="text-sm text-gray-600">{protocol.dosage} - {protocol.route}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedProtocol(protocol);
                      setShowAdminForm(true);
                    }}
                    disabled={!canAdmin.canAdminister}
                    className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                      canAdmin.canAdminister
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Pill className="w-4 h-4" />
                    Administer PRN
                  </button>
                </div>

                {/* Protocol Details */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Circumstances for Use</p>
                    <p className="font-medium text-gray-900">{protocol.circumstancesForUse}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Symptoms/Indications</p>
                    <p className="font-medium text-gray-900">{protocol.symptomsIndications}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Time Interval Between Doses</p>
                    <p className="font-medium text-gray-900">
                      {protocol.timeIntervalBetweenDoses ? `${protocol.timeIntervalBetweenDoses} hours` : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Maximum Dose (24h)</p>
                    <p className="font-medium text-gray-900">
                      {protocol.maximumDose24Hours || 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="flex gap-4 pt-4 border-t border-gray-200">
                  {hoursSince !== null && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        Last given: {hoursSince.toFixed(1)} hours ago
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      Today: {todayCount} {todayCount === 1 ? 'dose' : 'doses'}
                    </span>
                  </div>
                  {!canAdmin.canAdminister && (
                    <div className="flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-sm font-medium">{canAdmin.reason}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Pill className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-500">No PRN protocols configured</p>
          <p className="text-sm text-gray-400 mt-2">Create a PRN protocol to track as-needed medication</p>
        </div>
      )}

      {/* Recent Administrations */}
      {administrations.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Recent PRN Administrations</h4>
          <div className="space-y-3">
            {administrations.slice(0, 10).map(admin => (
              <div key={admin.id} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(admin.actualAdministrationTime || admin.createdAt).toLocaleString()}
                    </p>
                    {admin.prnReason && (
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Reason:</strong> {admin.prnReason}
                      </p>
                    )}
                    {admin.prnSymptoms && (
                      <p className="text-sm text-gray-600">
                        <strong>Symptoms:</strong> {admin.prnSymptoms}
                      </p>
                    )}
                    {admin.administeringWorkerName && (
                      <p className="text-xs text-gray-500 mt-1">
                        Administered by: {admin.administeringWorkerName}
                      </p>
                    )}
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PRN Protocol Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">New PRN Protocol</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateProtocol} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Medication Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={protocolFormData.medicationName}
                    onChange={(e) => setProtocolFormData({...protocolFormData, medicationName: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Dosage <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={protocolFormData.dosage}
                    onChange={(e) => setProtocolFormData({...protocolFormData, dosage: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 1 tablet, 5ml"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Circumstances for Use <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={protocolFormData.circumstancesForUse}
                  onChange={(e) => setProtocolFormData({...protocolFormData, circumstancesForUse: e.target.value})}
                  required
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe when this PRN medication should be given..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Symptoms/Indications <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={protocolFormData.symptomsIndications}
                  onChange={(e) => setProtocolFormData({...protocolFormData, symptomsIndications: e.target.value})}
                  required
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="What symptoms indicate this medication should be given..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Minimum Time Interval (hours)
                  </label>
                  <input
                    type="number"
                    value={protocolFormData.timeIntervalBetweenDoses}
                    onChange={(e) => setProtocolFormData({...protocolFormData, timeIntervalBetweenDoses: e.target.value})}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 4"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Maximum Dose (24 hours)
                  </label>
                  <input
                    type="number"
                    value={protocolFormData.maximumDose24Hours}
                    onChange={(e) => setProtocolFormData({...protocolFormData, maximumDose24Hours: e.target.value})}
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., 3"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Circumstances Requiring GP Notification
                </label>
                <textarea
                  value={protocolFormData.circumstancesRequiringGPNotification}
                  onChange={(e) => setProtocolFormData({...protocolFormData, circumstancesRequiringGPNotification: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="When should the GP be notified..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="supervisorNotification"
                  checked={protocolFormData.requiresSupervisorNotification}
                  onChange={(e) => setProtocolFormData({...protocolFormData, requiresSupervisorNotification: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="supervisorNotification" className="text-sm font-semibold text-gray-700">
                  Requires Supervisor Notification (S8/Sedatives/Psychotropics)
                </label>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Protocol'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PRN Administration Form Modal */}
      {showAdminForm && selectedProtocol && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Administer PRN Medication</h3>
              <button
                onClick={() => {
                  setShowAdminForm(false);
                  setSelectedProtocol(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="font-semibold text-blue-900">{selectedProtocol.medicationName}</p>
              <p className="text-sm text-blue-700">{selectedProtocol.dosage} - {selectedProtocol.route}</p>
            </div>

            <form onSubmit={handleAdministerPRN} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason for Administration <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={adminFormData.reason}
                  onChange={(e) => setAdminFormData({...adminFormData, reason: e.target.value})}
                  required
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Why is this PRN medication being given now?"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Symptoms <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={adminFormData.symptoms}
                  onChange={(e) => setAdminFormData({...adminFormData, symptoms: e.target.value})}
                  required
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="What symptoms is the participant experiencing?"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Time Given <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={adminFormData.timeGiven}
                    onChange={(e) => setAdminFormData({...adminFormData, timeGiven: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Administering Worker <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    value={adminFormData.administeringWorker}
                    onChange={(e) => setAdminFormData({...adminFormData, administeringWorker: e.target.value})}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Outcome Observations
                </label>
                <textarea
                  value={adminFormData.outcomeObservations}
                  onChange={(e) => setAdminFormData({...adminFormData, outcomeObservations: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Document response and observations..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Side Effects Observed
                </label>
                <textarea
                  value={adminFormData.sideEffects}
                  onChange={(e) => setAdminFormData({...adminFormData, sideEffects: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Any side effects observed..."
                />
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdminForm(false);
                    setSelectedProtocol(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Pill className="w-4 h-4" />
                  {loading ? 'Recording...' : 'Record Administration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PRNMedicationTracking;
